/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonFilterERP,
  ButtonPrimeReact,
  Checkbox,
  SearchInput,
  TagERP,
} from "@finanedi.es/storybook"
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AsientosContablesQueryParamsProps } from "api/AsientoContable.service"
import { GetAllProvinciasDtoProps } from "api/DTOs/GetAllProvinciasDto"
import { GetEmpresasGestionadasAsesoriaByFiltersDtoProps } from "api/DTOs/GetEmpresasGestionadasAsesoriaByFiltersDto"
import { GetEmpresasGestionadasAsesoriaByFiltersQueryParamsProps } from "api/EmpresaGestionadaAsesoria.service"
import { GetIndustriaReadingDto } from "api/Industria.service"
import { GetAllProvinciasDtoIPagedEnumerable } from "api/Provincia.service"
import { GetAllProvinciasDtoIPagedEnumerableProps } from "api/Schemas/GetAllProvinciasDtoIPagedEnumerable"
import { ESTADO_INVITACION_ASESORIA } from "api/Types/EstadoInvitacionAsesoria"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { Dispatch, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FilteredList, useFilteredList } from "../components/FilteredList"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
}: {
  serviceQuery: Dispatch<
    Partial<GetEmpresasGestionadasAsesoriaByFiltersDtoProps>
  >
  sortingCriteria?: SortingCriteria
  skipCount?: AsientosContablesQueryParamsProps["skipCount"]
  maxResultCount?: AsientosContablesQueryParamsProps["maxResultCount"]
  resetPagination?: () => void
}) => {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [keyword, setKeyword] = useState<string | undefined>()
  const [estado, setEstado] = useState({
    [ESTADO_INVITACION_ASESORIA.PENDIENTE]: false,
    [ESTADO_INVITACION_ASESORIA.ACEPTADA]: false,
    [ESTADO_INVITACION_ASESORIA.RECHAZADA]: false,
  })
  const handleEstado = (checkbox: keyof typeof estado) => {
    setEstado((prevEstado) => ({
      ...prevEstado,
      [checkbox]: !prevEstado[checkbox],
    }))
  }
  const {
    filteredList: provinciasFiltered,
    itemsSelected: provinciasSelected,
    searchItem: searchProvincia,
    setItemsSelected: setProvinciasSelected,
    onSearchItem: onSearchProvincia,
  } = useFilteredList<
    GetAllProvinciasDtoIPagedEnumerableProps,
    GetAllProvinciasDtoProps
  >({
    serviceCall: GetAllProvinciasDtoIPagedEnumerable,
    adapter: (data) => data?.items ?? [],
    label: "nombre",
  })
  const {
    filteredList: actividadFiltered,
    itemsSelected: actividadSelected,
    searchItem: searchActividad,
    setItemsSelected: setActividadSelected,
    onSearchItem: onSearchActividad,
  } = useFilteredList({
    serviceCall: GetIndustriaReadingDto,
    adapter: (data) => data ?? [],
    label: "descripcion",
  })

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (
    queryParams: GetEmpresasGestionadasAsesoriaByFiltersQueryParamsProps
  ) => {
    const asesoriaParams = {
      IndustriaIds: queryParams.IndustriaIds,
      EstadoInvitaciones: queryParams.EstadoInvitaciones,
      Keyword: queryParams.Keyword,
      ProvinciaIds: queryParams.ProvinciaIds,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(asesoriaParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    EstadoInvitaciones: Object.entries(estado)
      .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
      .flatMap(([key]) =>
        key === `${ESTADO_INVITACION_ASESORIA.PENDIENTE}`
          ? ESTADO_INVITACION_ASESORIA.PENDIENTE
          : key === `${ESTADO_INVITACION_ASESORIA.ACEPTADA}`
          ? ESTADO_INVITACION_ASESORIA.ACEPTADA
          : key === `${ESTADO_INVITACION_ASESORIA.RECHAZADA}`
          ? ESTADO_INVITACION_ASESORIA.RECHAZADA
          : []
      ),
    IndustriaIds: actividadSelected?.flatMap((actividad) => actividad.id),
    ProvinciaIds: provinciasSelected?.flatMap((provincia) => provincia.id),
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setEstado({
      [ESTADO_INVITACION_ASESORIA.PENDIENTE]: false,
      [ESTADO_INVITACION_ASESORIA.ACEPTADA]: false,
      [ESTADO_INVITACION_ASESORIA.RECHAZADA]: false,
    })
    setProvinciasSelected([])
    setActividadSelected([])
  }
  const mapProvinciasTags = provinciasSelected?.map((provincia) => ({
    label: provincia.nombre
      ? `${t("empresasGestionadas.filterMenu.tags.province", {
          province: provincia.nombre,
        })}`
      : "",
    value: true,
    action: () =>
      setProvinciasSelected(
        provinciasSelected?.filter(
          (selectedProvincia) => selectedProvincia !== provincia
        )
      ),
  }))
  const mapActividadTags = actividadSelected?.map((actividad) => ({
    label: actividad.descripcion
      ? `${t("empresasGestionadas.filterMenu.tags.activity", {
          activity: actividad.descripcion,
        })}`
      : "",
    value: true,
    action: () =>
      setActividadSelected(
        actividadSelected?.filter(
          (selectedActividad) => selectedActividad !== actividad
        )
      ),
  }))
  const appliedFiltersTags = [
    {
      label: t("empresasGestionadas.filterMenu.tags.status", {
        status: t("empresasGestionadas.filterMenu.status.validated"),
      }),
      value: estado[ESTADO_INVITACION_ASESORIA.ACEPTADA] ?? false,
      action: () => handleEstado(ESTADO_INVITACION_ASESORIA.ACEPTADA),
    },
    {
      label: t("empresasGestionadas.filterMenu.tags.status", {
        status: t("empresasGestionadas.filterMenu.status.unvalidated"),
      }),
      value: estado[ESTADO_INVITACION_ASESORIA.PENDIENTE] ?? false,
      action: () => handleEstado(ESTADO_INVITACION_ASESORIA.PENDIENTE),
    },
    {
      label: t("empresasGestionadas.filterMenu.tags.status", {
        status: t("empresasGestionadas.filterMenu.status.rejected"),
      }),
      value: estado[ESTADO_INVITACION_ASESORIA.RECHAZADA] ?? false,
      action: () => handleEstado(ESTADO_INVITACION_ASESORIA.RECHAZADA),
    },
    ...(mapProvinciasTags ?? []),
    ...(mapActividadTags ?? []),
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, skipCount, maxResultCount, appliedFilters?.length])

  return (
    <>
      <SearchInput
        placeholder={`${t("empresasGestionadas.header.searchPlaceholder")}`}
        onChange={(ev: string) => {
          resetPagination?.()
          setKeyword(ev)
        }}
        inputValue={keyword}
        className='tw-h-10 tw-w-full sm:tw-w-72'
      />
      <ButtonFilterERP
        isActive={appliedFilters.length > 0}
        numberActive={appliedFilters?.length}
        onClick={openFilters}
        deleteFilter={handleResetFilters}
        filterButtonLabel={`${t("filtros.title")}`}
      />

      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='!tw-m-0 !tw-h-full tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary hover:!tw-bg-primary-over tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("empresasGestionadas.filterMenu.status.title")}
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-validada'
                className='tw-mr-4'
                isChecked={estado[ESTADO_INVITACION_ASESORIA.ACEPTADA]}
                onClick={() =>
                  handleEstado(ESTADO_INVITACION_ASESORIA.ACEPTADA)
                }
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() =>
                  handleEstado(ESTADO_INVITACION_ASESORIA.ACEPTADA)
                }
              >
                <TagERP
                  value={t("empresasGestionadas.filterMenu.status.validated")}
                  color='success'
                  icon={<FontAwesomeIcon icon={faCircleCheck} />}
                />
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-sinValidar'
                className='tw-mr-4'
                isChecked={estado[ESTADO_INVITACION_ASESORIA.PENDIENTE]}
                onClick={() =>
                  handleEstado(ESTADO_INVITACION_ASESORIA.PENDIENTE)
                }
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() =>
                  handleEstado(ESTADO_INVITACION_ASESORIA.PENDIENTE)
                }
              >
                <TagERP
                  value={t("empresasGestionadas.filterMenu.status.unvalidated")}
                  color='alternativeOne'
                  icon={<FontAwesomeIcon icon={faClock} />}
                />
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-rechazadas'
                className='tw-mr-4'
                isChecked={estado[ESTADO_INVITACION_ASESORIA.RECHAZADA]}
                onClick={() =>
                  handleEstado(ESTADO_INVITACION_ASESORIA.RECHAZADA)
                }
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() =>
                  handleEstado(ESTADO_INVITACION_ASESORIA.RECHAZADA)
                }
              >
                <TagERP
                  value={t("empresasGestionadas.filterMenu.status.rejected")}
                  color='danger'
                  icon={<FontAwesomeIcon icon={faCircleXmark} />}
                />
              </span>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("empresasGestionadas.filterMenu.province")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col tw-mb-8'>
              <FilteredList
                inputId='empresasGestionadas-filterMenu-province'
                filteredList={provinciasFiltered}
                itemsSelected={provinciasSelected}
                searchItem={searchProvincia}
                setItemsSelected={setProvinciasSelected}
                onSearchItem={onSearchProvincia}
                label='nombre'
              />
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("empresasGestionadas.filterMenu.activity")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col tw-mb-8'>
              <FilteredList
                inputId='empresasGestionadas-filterMenu-activity'
                filteredList={actividadFiltered}
                itemsSelected={actividadSelected}
                searchItem={searchActividad}
                setItemsSelected={setActividadSelected}
                onSearchItem={onSearchActividad}
                label='descripcion'
              />
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
